import { RegisteredComponent } from "@builder.io/sdk-react";
import { TITLE_FIELD, HREF_FIELD, DLT_FIELD, DLD_FIELD } from "../commonFields";

export const globalNavigationBlockConfig: Omit<
  RegisteredComponent,
  "component"
> = {
  name: "Navigation",
  noWrap: true,
  shouldReceiveBuilderProps: {
    builderBlock: true,
    builderComponents: true,
    builderLinkComponent: true,
    builderContext: true,
  },
  canHaveChildren: false,
  models: ["page-header"],
  inputs: [
    {
      name: "isConsolidatedNavigation",
      type: "boolean",
      defaultValue: false,
      helperText:
        "Enables the consolidated navigation. When enabled, a toggle for docked rendering is available.",
    },
    {
      name: "isDocked",
      type: "boolean",
      defaultValue: false,
      helperText: "Enables docked rendering.",
      showIf: (options) => options.get("isConsolidatedNavigation") === true,
    },
    {
      name: "loginMenu",
      friendlyName: "Login Menu",
      type: "object",
      showIf: (options) => options.get("isConsolidatedNavigation") === true,
      subFields: [
        {
          name: "dlt",
          type: "string",
          required: false,
        },
        {
          name: "dld",
          type: "string",
          required: false,
        },
        {
          name: "links",
          type: "list",
          subFields: [
            { ...TITLE_FIELD, required: true },
            { ...HREF_FIELD, required: true },
            { ...DLT_FIELD, required: true },
            { ...DLD_FIELD, required: true },
          ],
        },
        { name: "ariaLabel", type: "string" },
      ],
      defaultValue: {
        ariaLabel: "Login Menu",
        dlt: "Top Menu",
        dld: "Login",
        links: [
          {
            title: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "Partner Portal Login",
            },
            href: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "https://partner.solarwinds.com/",
            },
            dlt: "Top Menu",
            dld: "Login:Partner Portal Login",
          },
          {
            title: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "Customer Portal Login",
            },
            href: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "https://customerportal.solarwinds.com/",
            },
            dlt: "Top Menu",
            dld: "Login:Customer Portal Login",
          },
        ],
      },
    },
    {
      name: "contactLink",
      friendlyName: "Contact Link",
      type: "object",
      showIf: (options) => options.get("isConsolidatedNavigation") === true,
      subFields: [
        {
          name: "title",
          type: "string",
          required: false,
          localized: true,
        },
        {
          name: "href",
          type: "string",
          required: false,
          localized: true,
        },
        {
          name: "dlt",
          type: "string",
          required: false,
        },
        {
          name: "dld",
          type: "string",
          required: false,
        },
      ],
      defaultValue: {
        title: {
          "@type": "@builder.io/core:LocalizedValue",
          Default: "Contact Us",
        },
        href: {
          "@type": "@builder.io/core:LocalizedValue",
          Default: "https://www.solarwinds.com/company/contact-us",
        },
        dlt: "Top Menu",
        dld: "Contact Us",
      },
    },
    {
      name: "superHeader",
      friendlyName: "Super Header",
      type: "object",
      subFields: [
        {
          name: "skipToMainContentLabel",
          type: "string",
          localized: true,
          required: true,
        },
        {
          name: "items",
          type: "list",
          subFields: [
            { ...TITLE_FIELD, required: true },
            {
              ...HREF_FIELD,
              required: false,
              helperText: "If left empty and has links is dropdown button.",
            },
            { ...DLT_FIELD, required: true },
            { ...DLD_FIELD, required: true },
            {
              name: "links",
              type: "list",
              subFields: [
                { ...TITLE_FIELD, required: true },
                { ...HREF_FIELD, required: true },
                { ...DLT_FIELD, required: true },
                { ...DLD_FIELD, required: true },
              ],
            },
          ],
        },
        {
          name: "languageSwitcher",
          type: "object",
          subFields: [
            {
              name: "languageButtonLabel",
              type: "string",
              localized: true,
              required: true,
              helperText:
                "For screen readers only. Please put {{lang}} if you want to have there current language.",
            },
            { name: "languageButtonDLD", type: "string", required: true },
            { name: "languageButtonDLT", type: "string", required: true },
            {
              name: "languages",
              type: "list",
              subFields: [
                {
                  name: "icon",
                  type: "AprimoAssetSelector",
                  options: {
                    searchingExpression:
                      'Contentstatus = "Released" AND ContentType = "Image" AND Classification.NamePath = "/DAM/Audience/External" AND Classification.NamePath = "/DAM/Status/Available"',
                  },
                  required: false,
                  localized: false,
                  helperText:
                    "Select the icon from the DAM (Aprimo). Only icons which are permitted to use on the website are selectable; therefore, archived, draft or internal assets can't be selected.",
                },
                {
                  name: "ariaLabel",
                  type: "string",
                  required: true,
                  localized: true,
                },
                { name: "languageCode", type: "string", required: true },
                { ...TITLE_FIELD, required: true },
                {
                  name: "href",
                  type: "string",
                  friendlyName: "Href",
                  localized: true,
                  required: true,
                  helperText:
                    "Enter the locale path segment only, for example de, fr, ja.",
                },
                { ...DLT_FIELD, required: true },
                { ...DLD_FIELD, required: true },
              ],
            },
          ],
        },
        {
          name: "search",
          type: "object",
          subFields: [
            {
              name: "showButtonLabel",
              type: "string",
              required: true,
              localized: true,
              helperText: "For screen readers only.",
            },
            {
              name: "showButtonDLD",
              type: "string",
              required: true,
            },
            {
              name: "showButtonDLT",
              type: "string",
              required: true,
            },
            {
              name: "hideButtonLabel",
              type: "string",
              required: true,
              localized: true,
              helperText: "For screen readers only.",
            },
            {
              name: "hideButtonDLD",
              type: "string",
              required: true,
            },
            {
              name: "hideButtonDLT",
              type: "string",
              required: true,
            },
            {
              name: "inputLabel",
              type: "string",
              required: true,
              localized: true,
              helperText: "For screen readers only.",
            },
            {
              name: "inputPlaceholder",
              type: "string",
              required: true,
              localized: true,
            },
            {
              name: "submitButtonLabel",
              type: "string",
              required: true,
              localized: true,
              helperText: "For screen readers only.",
            },
            {
              name: "submitButtonDLD",
              type: "string",
              required: true,
            },
            {
              name: "submitButtonDLT",
              type: "string",
              required: true,
            },
          ],
        },
      ],
      defaultValue: {
        skipToMainContentLabel: {
          "@type": "@builder.io/core:LocalizedValue",
          Default: "Skip to Main Content",
        },
        items: [
          {
            title: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "Partners",
            },
            dlt: "Default Header",
            dld: "TopLink1:Partners",
            links: [
              {
                title: {
                  "@type": "@builder.io/core:LocalizedValue",
                  Default: "Portal Login",
                },
                href: {
                  "@type": "@builder.io/core:LocalizedValue",
                  Default:
                    "https://partner.solarwinds.com/?_gl=1*14tdq83*_gcl_au*MjE3NzA3MDUwLjE3NjU3ODM3OTE.*_ga*MTQ2NjYzMDQ0OS4xNzY1NzgzNzkx",
                },
                dlt: "Default Header",
                dld: "TopLink1:Partners:Portal Login",
              },
              {
                title: {
                  "@type": "@builder.io/core:LocalizedValue",
                  Default: "Program Overview",
                },
                href: {
                  "@type": "@builder.io/core:LocalizedValue",
                  Default: "https://www.solarwinds.com/partners",
                },
                dlt: "Default Header",
                dld: "TopLink1:Partners:Program Overview",
              },
              {
                title: {
                  "@type": "@builder.io/core:LocalizedValue",
                  Default: "Become a Partner",
                },
                href: {
                  "@type": "@builder.io/core:LocalizedValue",
                  Default: "https://partner.solarwinds.com/",
                },
                dlt: "Default Header",
                dld: "TopLink1:Partners:Become a Partner",
              },
              {
                title: {
                  "@type": "@builder.io/core:LocalizedValue",
                  Default: "Find a Partner",
                },
                href: {
                  "@type": "@builder.io/core:LocalizedValue",
                  Default: "https://www.solarwinds.com/partners/locator",
                },
                dlt: "Default Header",
                dld: "TopLink1:Partners:Find a Partner",
              },
              {
                title: {
                  "@type": "@builder.io/core:LocalizedValue",
                  Default: "Global Systems Integrators",
                },
                href: {
                  "@type": "@builder.io/core:LocalizedValue",
                  Default:
                    "https://www.solarwinds.com/partners/global-system-integrators",
                },
                dlt: "Default Header",
                dld: "TopLink1:Partners:Global Systems Integrators",
              },
              {
                title: {
                  "@type": "@builder.io/core:LocalizedValue",
                  Default: "Strategic Alliances",
                },
                href: {
                  "@type": "@builder.io/core:LocalizedValue",
                  Default:
                    "https://www.solarwinds.com/partners/strategic-alliances",
                },
                dlt: "Default Header",
                dld: "TopLink1:Partners:Strategic Alliances",
              },
            ],
          },
          {
            title: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "Customer Portal",
            },
            href: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "https://customerportal.solarwinds.com/",
            },
            dlt: "Default Header",
            dld: "TopLink2:Customer Portal",
          },
          {
            title: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "THWACK®",
            },
            href: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "https://thwack.solarwinds.com/",
            },
            dlt: "Default Header",
            dld: "TopLink3:THWACK®",
          },
          {
            title: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "Events",
            },
            href: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "https://www.solarwinds.com/events",
            },
            dlt: "Default Header",
            dld: "TopLink4:Events",
          },
          {
            title: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "SolarWinds Blog",
            },
            href: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "https://www.solarwinds.com/blog",
            },
            dlt: "Default Header",
            dld: "TopLink5:SolarWinds Blog",
          },
          {
            title: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "Public Sector",
            },
            href: {
              "@type": "@builder.io/core:LocalizedValue",
              Default:
                "https://www.solarwinds.com/public-sector/it-management-solutions-for-government",
            },
            dlt: "Default Header",
            dld: "TopLink6:Public Sector",
          },
          {
            title: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "Contact Us",
            },
            href: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "https://www.solarwinds.com/company/contact-us",
            },
            dlt: "Default Header",
            dld: "TopLink7:Contact Us",
          },
        ],
        languageSwitcher: {
          languageButtonLabel: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "Current language {{lang}}, Change Language",
          },
          languageButtonDLD: "language switcher",
          languageButtonDLT: "language switcher",
          languages: [
            {
              ariaLabel: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "English",
              },
              languageCode: "en-us",
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "English",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "en",
              },
              icon: "https://p1.aprimocdn.net/solarwinds/afe27960-cb9b-4f47-8c00-b41000fc82b9/USA-Flag_Original%20file.svg",
              dlt: "Default Header",
              dld: "TopLink7:Language:EN",
            },
            {
              ariaLabel: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "German",
              },
              languageCode: "de",
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Deutsch",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "de",
              },
              icon: "https://p1.aprimocdn.net/solarwinds/5f23a12d-18c1-49cb-8811-b40a00de992d/German_Flag_Original%20file.svg",
              dlt: "Default Header",
              dld: "TopLink7:Language:DE",
            },
            {
              ariaLabel: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Spanish",
              },
              languageCode: "es",
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Español",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "es",
              },
              icon: "https://p1.aprimocdn.net/solarwinds/19da2192-d592-4a3e-94bc-b40a00dea757/Mexico-Flag_Original%20file.svg",
              dlt: "Default Header",
              dld: "TopLink7:Language:ES",
            },
            {
              ariaLabel: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "French",
              },
              languageCode: "fr",
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Français",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "fr",
              },
              icon: "https://p1.aprimocdn.net/solarwinds/5216db2b-28ef-45c9-8c6d-b40a00dea9ea/France-Flag_Original%20file.svg",
              dlt: "Default Header",
              dld: "TopLink7:Language:FR",
            },
            {
              ariaLabel: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Japanese",
              },
              languageCode: "ja",
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "日本語",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "ja",
              },
              icon: "https://p1.aprimocdn.net/solarwinds/27f1fc3d-cae0-4419-ba86-b40a00dea970/Japan-Flag_Original%20file.svg",
              dlt: "Default Header",
              dld: "TopLink7:Language:JA",
            },
            {
              ariaLabel: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Korean",
              },
              languageCode: "ko",
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "한국어",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "ko",
              },
              icon: "https://p1.aprimocdn.net/solarwinds/27cad5ac-6407-403c-8f79-b40a00dea93e/Korea-Flag_Original%20file.svg",
              dlt: "Default Header",
              dld: "TopLink7:Language:KO",
            },
            {
              ariaLabel: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Portuguese",
              },
              languageCode: "pt",
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Português",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "pt",
              },
              icon: "https://p1.aprimocdn.net/solarwinds/30f79e9a-f507-4f21-9bce-b40a00dea85e/Portugal-Flag_Original%20file.svg",
              dlt: "Default Header",
              dld: "TopLink7:Language:PT",
            },
            {
              ariaLabel: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Chinese",
              },
              languageCode: "zh",
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "中文",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "zh",
              },
              icon: "https://p1.aprimocdn.net/solarwinds/c01d2ece-6868-40c7-a3ff-b40a00dea7f9/China-Flag_Original%20file.svg",
              dlt: "Default Header",
              dld: "TopLink7:Language:ZH",
            },
          ],
        },
        search: {
          showButtonLabel: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "Show Search box",
          },
          showButtonDLD: "search button",
          showButtonDLT: "search",
          hideButtonLabel: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "Hide Search box",
          },
          hideButtonDLD: "search exit",
          hideButtonDLT: "search",
          inputLabel: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "Input Search box",
          },
          inputPlaceholder: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "Search...",
          },
          submitButtonLabel: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "Submit Search box",
          },
          submitButtonDLD: "submit button",
          submitButtonDLT: "search",
        },
      },
    },
    {
      name: "logo",
      type: "uiBlocks",
      defaultValue: [
        {
          "@type": "@builder.io/sdk:Element",
          component: {
            name: "Interactive Block",
            options: {
              link: {
                href: {
                  "@type": "@builder.io/core:LocalizedValue",
                  Default: "https://www.solarwinds.com",
                },
                dld: "SolarWinds Logo, return to the Home page.",
                dlt: "Default Header",
              },
            },
          },
          children: [
            {
              "@type": "@builder.io/sdk:Element",
              component: {
                name: "Image",
                options: {
                  priority: true,
                  alt: "Solarwinds header logo",
                  source: "aprimo",
                  image:
                    "https://p1.aprimocdn.net/solarwinds/94ce9bf7-6584-478a-bca6-b23400fa3359/SW_Logo_Web_Orange_XS_downloadPNG.png",
                },
              },
            },
          ],
          responsiveStyles: {
            large: { maxWidth: "147px" },
            medium: { maxWidth: "150px" },
          },
        },
      ],
    },
    {
      name: "mobileLogoExpanded",
      type: "uiBlocks",
      defaultValue: [
        {
          "@type": "@builder.io/sdk:Element",
          component: {
            name: "Interactive Block",
            options: {
              link: {
                href: {
                  "@type": "@builder.io/core:LocalizedValue",
                  Default: "https://www.solarwinds.com",
                },
                dld: "SolarWinds Logo, return to the Home page.",
                dlt: "Default Header",
              },
            },
          },
          children: [
            {
              "@type": "@builder.io/sdk:Element",
              component: {
                name: "Icon",
                options: {
                  priority: true,
                  alt: "Solarwinds header logo",
                  source: "aprimo",
                  icon: "https://p1.aprimocdn.net/solarwinds/b0a24941-e427-43df-ab04-b2420142eddf/SW_Logo_Flame_Web_Orange_Original%20file.svg",
                  width: 43,
                  height: 36,
                },
              },
            },
          ],
        },
      ],
    },
    {
      name: "cta",
      type: "uiBlocks",
      defaultValue: [
        {
          "@type": "@builder.io/sdk:Element",
          component: {
            name: "Core:Button",
            options: {
              variant: "primary",
              size: "s",
              text: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Request Quote",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/onlinequotes",
              },
              dld: "Request Quote",
              dlt: "Top Menu",
            },
          },
          responsiveStyles: {
            large: { textAlign: "center" },
          },
        },
      ],
    },
    {
      name: "mobileHamburgerMenuCollapsedDLD",
      type: "string",
      helperText: "DLD when menu is collapsed.",
      defaultValue: "MobileMenu:Expand",
    },
    {
      name: "mobileHamburgerMenuCollapsedDLT",
      type: "string",
      helperText: "DLT when menu is collapsed.",
      defaultValue: "Top Menu",
    },
    {
      name: "mobileHamburgerMenuExpandedDLD",
      type: "string",
      helperText: "DLD when menu is expanded.",
      defaultValue: "MobileMenu:Collapse",
    },
    {
      name: "mobileHamburgerMenuExpandedDLT",
      type: "string",
      helperText: "DLT when menu is expanded.",
      defaultValue: "Top Menu",
    },
    {
      name: "mobileBackLink",
      type: "object",
      subFields: [
        { name: "dlt", type: "string" },
        { name: "dld", type: "string" },
        { name: "backToText", type: "string", localized: true },
        { name: "mainMenuText", type: "string", localized: true },
      ],
      defaultValue: {
        dlt: "",
        dld: "",
        backToText: {
          "@type": "@builder.io/core:LocalizedValue",
          Default: "Back to",
        },
        mainMenuText: {
          "@type": "@builder.io/core:LocalizedValue",
          Default: "Main Menu",
        },
      },
    },
    {
      name: "colorVariant",
      type: "enum",
      enum: [
        { label: "cool gray", value: "coolGray" },
        { label: "blue", value: "blue" },
        { label: "gray", value: "gray" },
      ],
      defaultValue: "blue",
    },
    {
      name: "menuItems",
      type: "list",
      subFields: [
        { ...TITLE_FIELD, required: true },
        {
          ...HREF_FIELD,
          required: false,
          helperText: "If left empty is dropdown button.",
        },
        { ...DLT_FIELD, required: true },
        { ...DLD_FIELD, required: true },
        { name: "leftContent", type: "uiBlocks", defaultValue: [] },
        { name: "leftCta", type: "uiBlocks", defaultValue: [] },
        {
          name: "leftLinksTitle",
          type: "string",
          localized: true,
        },
        {
          name: "leftLinks",
          type: "list",
          subFields: [
            { ...TITLE_FIELD, required: true },
            { ...HREF_FIELD, required: true },
            { ...DLT_FIELD, required: true },
            { ...DLD_FIELD, required: true },
          ],
        },
        {
          name: "categoryLinksInTwoColumns",
          type: "boolean",
          defaultValue: false,
        },
        {
          name: "categoryLinks",
          type: "list",
          subFields: [
            {
              name: "categoryTitle",
              type: "string",
              localized: true,
              required: true,
            },
            {
              name: "categoryTitleMobileDLD",
              type: "string",
              localized: false,
            },
            {
              name: "categoryTitleMobileDLT",
              type: "string",
              localized: false,
            },
            {
              name: "categoryDescription",
              type: "uiBlocks",
              defaultValue: [],
            },
            { name: "icon", type: "uiBlocks", defaultValue: [] },
            { name: "cta", type: "uiBlocks", defaultValue: [] },
            {
              name: "linksColumnCount",
              type: "enum",
              enum: [
                { label: "1", value: "1" },
                { label: "2", value: "2" },
                { label: "3", value: "3" },
              ],
              defaultValue: "2",
            },
            {
              name: "links",
              type: "list",
              subFields: [
                { ...TITLE_FIELD, required: true },
                { ...HREF_FIELD, required: true },
                { ...DLT_FIELD, required: true },
                { ...DLD_FIELD, required: true },
              ],
            },
          ],
        },
        {
          name: "bottomLinksTitle",
          type: "string",
          localized: true,
        },
        {
          name: "bottomLinks",
          type: "list",
          subFields: [
            { ...TITLE_FIELD, required: true },
            { ...HREF_FIELD, required: true },
            { ...DLT_FIELD, required: true },
            { ...DLD_FIELD, required: true },
          ],
        },
      ],
      defaultValue: [
        {
          title: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "Platform",
          },
          dlt: "top menu",
          dld: "Platform",
          leftContent: [
            {
              "@type": "@builder.io/sdk:Element",
              component: {
                name: "Text",
                options: {
                  text: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "<p><strong>Bring IT all together</strong></p>",
                  },
                },
              },
              meta: {
                localizedTextInputs: ["text"],
                "transformed.text": "localized",
              },
              responsiveStyles: {
                large: {
                  lineHeight: "var(--text-subhead-xl--line-height, 2.5rem)",
                  fontSize: "var(--text-subhead-xl, 1.875rem)",
                  color: "var(--white, #fff)",
                  marginBottom: "var(--spacing-2, 8px)",
                },
              },
            },
            {
              "@type": "@builder.io/sdk:Element",
              component: {
                name: "Text",
                options: {
                  text: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      '<p><span style="font-size: 16px;">Customers rely on SolarWinds to drive operational resilience, from unified observability to enterprise-wide service management, through a simple, powerful, and secure portfolio built for hybrid IT.</span></p>',
                  },
                },
              },
              meta: {
                localizedTextInputs: ["text"],
                "transformed.text": "localized",
              },
              responsiveStyles: {
                large: {
                  lineHeight: "var(--text-body-s--line-height, 1.5rem)",
                  fontSize: "var(--text-body-s, 1rem)",
                  color: "var(--white, #fff)",
                  marginBottom: "var(--spacing-6, 24px)",
                },
              },
            },
          ],
          leftCta: [
            {
              "@type": "@builder.io/sdk:Element",
              component: {
                name: "Core:Button",
                options: {
                  variant: "primary",
                  size: "m",
                  text: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Platform Overview",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/solarwinds-platform",
                  },
                  theme: "black",
                  dld: "platform overview button solarwinds platform",
                  dlt: "Top Menu",
                },
              },
              responsiveStyles: {
                large: {
                  marginBottom: "var(--spacing-10, 40px)",
                  width: "fit-content",
                },
                medium: { display: "none" },
                small: { display: "none" },
              },
            },
            {
              "@type": "@builder.io/sdk:Element",
              component: {
                name: "Core:Button",
                options: {
                  variant: "secondary",
                  size: "m",
                  text: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Platform Overview",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/solarwinds-platform",
                  },
                  theme: "white",
                  dld: "platform overview button solarwinds platform",
                  dlt: "Top Menu",
                },
              },
              responsiveStyles: {
                large: {
                  marginBottom: "var(--spacing-10, 40px)",
                  display: "none",
                },
                medium: { display: "flex", maxWidth: "fit-content" },
                small: { display: "flex" },
              },
            },
          ],
          leftLinksTitle: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "Explore SolarWinds",
          },
          leftLinks: [
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Interactive Demos",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/interactive-demos",
              },
              dlt: "top menu",
              dld: "Platform explore solarwinds Interactive Demos",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Pricing",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/pricing",
              },
              dlt: "top menu",
              dld: "Platform explore solarwinds Pricing",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "About Us",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/company",
              },
              dlt: "top menu",
              dld: "Platform explore solarwinds About Us",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Secure by Design",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default:
                  "https://www.solarwinds.com/secure-by-design-resources",
              },
              dlt: "top menu",
              dld: "Platform explore solarwinds Secure by Design",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "SolarWinds AI",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/ai",
              },
              dlt: "top menu",
              dld: "Platform explore solarwinds AI",
            },
          ],
          categoryLinksInTwoColumns: false,
          categoryLinks: [
            {
              categoryTitle: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Monitoring and Observability",
              },
              categoryTitleMobileDLD: "",
              categoryTitleMobileDLT: "",
              categoryDescription: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Text",
                    options: {
                      text: {
                        "@type": "@builder.io/core:LocalizedValue",
                        Default:
                          "<p>World-class capabilities built for your hybrid IT ecosystem.</p>",
                      },
                    },
                  },
                  meta: {
                    localizedTextInputs: ["text"],
                    "transformed.text": "localized",
                  },
                  responsiveStyles: {
                    large: {
                      lineHeight: "var(--text-body-xs--line-height, 1.5rem)",
                      fontSize: "var(--text-body-xs, 0.875rem)",
                      color: "var(--color-body-primary, #444)",
                      fontWeight: "700",
                    },
                  },
                },
              ],
              icon: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Icon",
                    options: {
                      priority: true,
                      alt: "Monitoring and Observability icon",
                      source: "aprimo",
                      icon: "https://p1.aprimocdn.net/solarwinds/bb77cc2e-29a8-46e0-84bf-b23401530e61/2305_SWI_Icon_6i%20Pingdom%20Server%20Monitor_Original%20file.svg",
                      width: 32,
                      height: 32,
                    },
                  },
                },
              ],
              cta: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Core:Button",
                    options: {
                      variant: "tertiary",
                      icon: "circleArrowRight",
                      iconPosition: "right",
                      size: "m",
                      text: {
                        "@type": "@builder.io/core:LocalizedValue",
                        Default: "Monitoring and Observability Overview",
                      },
                      href: {
                        "@type": "@builder.io/core:LocalizedValue",
                        Default: "https://www.solarwinds.com/observability",
                      },
                      dld: "Platform:Monitoring and Observability:Monitoring and Observability Overview",
                      dlt: "Top Menu",
                    },
                  },
                  responsiveStyles: {
                    large: { marginTop: "var(--spacing-4, 16px)" },
                    medium: {
                      marginTop: "0px",
                      fontSize: "var(--text-link-m, 1.125rem)",
                      lineHeight: "var(--text-link-m--line-height, 1.5rem)",
                    },
                  },
                },
              ],
              linksColumnCount: "2",
              links: [
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Network",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/observability/network",
                  },
                  dlt: "Top Menu",
                  dld: "Platform:Monitoring and Observability:Network",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Digital Experience",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/observability/digital-experience-monitoring",
                  },
                  dlt: "Top Menu",
                  dld: "Platform:Monitoring and Observability:Digital Experience",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Infrastructure",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/observability/infrastructure",
                  },
                  dlt: "Top Menu",
                  dld: "Platform:Monitoring and Observability:Infrastructure",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Logs",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/observability/logs",
                  },
                  dlt: "Top Menu",
                  dld: "Platform:Monitoring and Observability:Logs",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Database",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/database",
                  },
                  dlt: "Top Menu",
                  dld: "Platform:Monitoring and Observability:Database",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Security",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/observability/security",
                  },
                  dlt: "Top Menu",
                  dld: "Platform:Monitoring and Observability:Security",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Applications",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/observability/applications",
                  },
                  dlt: "Top Menu",
                  dld: "Platform:Monitoring and Observability:Applications",
                },
              ],
            },
            {
              categoryTitle: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Database Observability",
              },
              categoryTitleMobileDLD: "",
              categoryTitleMobileDLT: "",
              categoryDescription: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Text",
                    options: {
                      text: {
                        "@type": "@builder.io/core:LocalizedValue",
                        Default:
                          "<p>Monitor, alert, triage, remediate, and prevent database issues from disrupting your business.</p>",
                      },
                    },
                  },
                  meta: {
                    localizedTextInputs: ["text"],
                    "transformed.text": "localized",
                  },
                  responsiveStyles: {
                    large: {
                      lineHeight: "var(--text-body-xs--line-height, 1.5rem)",
                      fontSize: "var(--text-body-xs, 0.875rem)",
                      color: "var(--color-body-primary, #444)",
                      fontWeight: "700",
                    },
                  },
                },
              ],
              icon: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Icon",
                    options: {
                      priority: true,
                      alt: "Database Observability icon",
                      source: "aprimo",
                      icon: "https://p1.aprimocdn.net/solarwinds/65584d88-926c-4f21-a77e-b2340153249e/2305_SWI_Icon_3.%20Database%20Management_Original%20file.svg",
                      width: 32,
                      height: 32,
                    },
                  },
                },
              ],
              cta: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Core:Button",
                    options: {
                      variant: "tertiary",
                      icon: "circleArrowRight",
                      iconPosition: "right",
                      size: "m",
                      text: {
                        "@type": "@builder.io/core:LocalizedValue",
                        Default: "Database Observability Overview",
                      },
                      href: {
                        "@type": "@builder.io/core:LocalizedValue",
                        Default: "https://www.solarwinds.com/database",
                      },
                      dld: "Platform:Database Observability Overview",
                      dlt: "Top Menu",
                    },
                  },
                  responsiveStyles: {
                    large: { marginTop: "var(--spacing-4, 16px)" },
                    medium: {
                      marginTop: "0px",
                      fontSize: "var(--text-link-m, 1.125rem)",
                      lineHeight: "var(--text-link-m--line-height, 1.5rem)",
                    },
                  },
                },
              ],
              linksColumnCount: "2",
              links: [
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Monitor",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/database/monitoring",
                  },
                  dlt: "Top Menu",
                  dld: "Platform:Database Observability:Monitor",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Optimize",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/database/optimize",
                  },
                  dlt: "Top Menu",
                  dld: "Platform:Database Observability:Optimize",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Diagnose",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/database/diagnose",
                  },
                  dlt: "Top Menu",
                  dld: "Platform:Database Observability:Diagnose",
                },

                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Everywhere",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/database/database-everywhere",
                  },
                  dlt: "Top Menu",
                  dld: "Platform:Database Observability:Everywhere",
                },
              ],
            },
            {
              categoryTitle: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Incident Response",
              },
              categoryTitleMobileDLD: "",
              categoryTitleMobileDLT: "",
              categoryDescription: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Text",
                    options: {
                      text: {
                        "@type": "@builder.io/core:LocalizedValue",
                        Default:
                          "<p>Build operational resilience and keep your systems online.</p>",
                      },
                    },
                  },
                  meta: {
                    localizedTextInputs: ["text"],
                    "transformed.text": "localized",
                  },
                  responsiveStyles: {
                    large: {
                      lineHeight: "var(--text-body-xs--line-height, 1.5rem)",
                      fontSize: "var(--text-body-xs, 0.875rem)",
                      color: "var(--color-body-primary, #444)",
                      fontWeight: "700",
                    },
                  },
                },
              ],
              icon: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Icon",
                    options: {
                      priority: true,
                      alt: "Database Observability icon",
                      source: "aprimo",
                      icon: "https://p1.aprimocdn.net/solarwinds/6238acb2-b2f9-4181-8c04-b3a101235290/Icon_-_Incident_Response_Original%20file.svg",
                      width: 32,
                      height: 32,
                    },
                  },
                },
              ],
              cta: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Core:Button",
                    options: {
                      variant: "tertiary",
                      icon: "circleArrowRight",
                      iconPosition: "right",
                      size: "m",
                      text: {
                        "@type": "@builder.io/core:LocalizedValue",
                        Default: "Incident Response Overview",
                      },
                      href: {
                        "@type": "@builder.io/core:LocalizedValue",
                        Default: "https://www.solarwinds.com/incident-response",
                      },
                      dld: "Platform: Incident Response: Incident Response Overview",
                      dlt: "Top Menu",
                    },
                  },
                  responsiveStyles: {
                    large: { marginTop: "var(--spacing-4, 16px)" },
                    medium: {
                      marginTop: "0px",
                      fontSize: "var(--text-link-m, 1.125rem)",
                      lineHeight: "var(--text-link-m--line-height, 1.5rem)",
                    },
                  },
                },
              ],
            },
            {
              categoryTitle: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "IT Service Management",
              },
              categoryTitleMobileDLD: "",
              categoryTitleMobileDLT: "",
              categoryDescription: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Text",
                    options: {
                      text: {
                        "@type": "@builder.io/core:LocalizedValue",
                        Default:
                          "<p>Get teams back to work faster with an experience built for IT—and loved by users.</p>",
                      },
                    },
                  },
                  meta: {
                    localizedTextInputs: ["text"],
                    "transformed.text": "localized",
                  },
                  responsiveStyles: {
                    large: {
                      lineHeight: "var(--text-body-xs--line-height, 1.5rem)",
                      fontSize: "var(--text-body-xs, 0.875rem)",
                      color: "var(--color-body-primary, #444)",
                      fontWeight: "700",
                    },
                  },
                },
              ],
              icon: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Icon",
                    options: {
                      priority: true,
                      alt: "Database Observability icon",
                      source: "aprimo",
                      icon: "https://p1.aprimocdn.net/solarwinds/64e33dd6-24f6-453b-973b-b23401530e6f/2305_SWI_Icon_5.%20IT%20Service%20Management_Original%20file.svg",
                      width: 32,
                      height: 32,
                    },
                  },
                },
              ],
              cta: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Core:Button",
                    options: {
                      variant: "tertiary",
                      icon: "circleArrowRight",
                      iconPosition: "right",
                      size: "m",
                      text: {
                        "@type": "@builder.io/core:LocalizedValue",
                        Default: "IT Service Management Overview",
                      },
                      href: {
                        "@type": "@builder.io/core:LocalizedValue",
                        Default: "https://www.solarwinds.com/itsm",
                      },
                      dld: "Platform:IT Service Management:IT Service Management Overview",
                      dlt: "Top Menu",
                    },
                  },
                  responsiveStyles: {
                    large: { marginTop: "var(--spacing-4, 16px)" },
                    medium: {
                      marginTop: "0px",
                      fontSize: "var(--text-link-m, 1.125rem)",
                      lineHeight: "var(--text-link-m--line-height, 1.5rem)",
                    },
                  },
                },
              ],
              linksColumnCount: "2",
              links: [
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Service Management",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/itsm/service-management",
                  },
                  dlt: "Top Menu",
                  dld: "Platform:IT Service Management:Service Management",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "AI & Automation",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/itsm/ai-and-automation",
                  },
                  dlt: "Top Menu",
                  dld: "Platform:IT Service Management:AI & Automation",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Assets & CMDB",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/itsm/asset-management",
                  },
                  dlt: "Top Menu",
                  dld: "Platform:IT Service Management:Assets & CMDB",
                },

                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Enterprise Service Management",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/itsm/esm",
                  },
                  dlt: "Top Menu",
                  dld: "Platform:IT Service Management:Enterprise Service Management",
                },
              ],
            },
          ],
          bottomLinksTitle: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "Not sure where to start?",
          },
          bottomLinks: [
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Get Expert Guidance",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/company/contact-us",
              },
              dlt: "Top Menu",
              dld: "Platform navigation link strip Get Expert Guidance",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Explore Tools",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/tools",
              },
              dlt: "Top Menu",
              dld: "Platform navigation link strip Explore Tools",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Request a Quote",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/onlinequotes",
              },
              dlt: "Top Menu",
              dld: "Platform navigation link strip Request a Quote",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Visit Our THWACK User Community",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://thwack.solarwinds.com/",
              },
              dlt: "Top Menu",
              dld: "Platform navigation link strip Visit Our THWACK User Community",
            },
          ],
        },
        {
          title: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "Products",
          },
          dlt: "top menu",
          dld: "Products",
          leftContent: [
            {
              "@type": "@builder.io/sdk:Element",
              component: {
                name: "Text",
                options: {
                  text: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "<p><strong>Respond even faster</strong></p>",
                  },
                },
              },
              meta: {
                localizedTextInputs: ["text"],
                "transformed.text": "localized",
              },
              responsiveStyles: {
                large: {
                  lineHeight: "var(--text-subhead-xl--line-height, 2.5rem)",
                  fontSize: "var(--text-subhead-xl, 1.875rem)",
                  color: "var(--white, #fff)",
                  marginBottom: "var(--spacing-2, 8px)",
                },
              },
            },
            {
              "@type": "@builder.io/sdk:Element",
              component: {
                name: "Text",
                options: {
                  text: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "<p><span>SolarWinds helps teams respond faster through AI-Powered observability, database performance, incident response, and ITSM solutions. Let our products work hard so you don’t have to.</span></p>",
                  },
                },
              },
              meta: {
                localizedTextInputs: ["text"],
                "transformed.text": "localized",
              },
              responsiveStyles: {
                large: {
                  lineHeight: "var(--text-body-s--line-height, 1.5rem)",
                  fontSize: "var(--text-body-s, 1rem)",
                  color: "var(--white, #fff)",
                  marginBottom: "var(--spacing-6, 24px)",
                },
              },
            },
          ],
          leftCta: [
            {
              "@type": "@builder.io/sdk:Element",
              component: {
                name: "Core:Button",
                options: {
                  variant: "primary",
                  size: "m",
                  text: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "All Products",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/products",
                  },
                  theme: "black",
                  dld: "Products explore solarwinds all products button",
                  dlt: "Top Menu",
                },
              },
              responsiveStyles: {
                large: {
                  marginBottom: "var(--spacing-10, 40px)",
                  width: "fit-content",
                },
                medium: { display: "none" },
                small: { display: "none" },
              },
            },
            {
              "@type": "@builder.io/sdk:Element",
              component: {
                name: "Core:Button",
                options: {
                  variant: "secondary",
                  size: "m",
                  text: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Platform Overview",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/solarwinds-platform",
                  },
                  theme: "white",
                  dld: "platform overview button solarwinds platform",
                  dlt: "Top Menu",
                },
              },
              responsiveStyles: {
                large: {
                  marginBottom: "var(--spacing-10, 40px)",
                  display: "none",
                },
                medium: { display: "flex", maxWidth: "fit-content" },
                small: { display: "flex" },
              },
            },
          ],
          leftLinksTitle: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "Explore SolarWinds",
          },
          leftLinks: [
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Interactive Demos",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/interactive-demos",
              },
              dlt: "top menu",
              dld: "Platform explore solarwinds Interactive Demos",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Pricing",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/pricing",
              },
              dlt: "top menu",
              dld: "Platform explore solarwinds Pricing",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "About Us",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/company",
              },
              dlt: "top menu",
              dld: "Platform explore solarwinds About Us",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Secure by Design",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default:
                  "https://www.solarwinds.com/secure-by-design-resources",
              },
              dlt: "top menu",
              dld: "Platform explore solarwinds Secure by Design",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "SolarWinds AI",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/ai",
              },
              dlt: "top menu",
              dld: "Platform explore solarwinds AI",
            },
          ],
          categoryLinksInTwoColumns: false,
          categoryLinks: [
            {
              categoryTitle: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Monitoring & Observability",
              },
              categoryTitleMobileDLD: "",
              categoryTitleMobileDLT: "",
              categoryDescription: [],
              icon: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Icon",
                    options: {
                      priority: true,
                      alt: "Monitoring and Observability icon",
                      source: "aprimo",
                      icon: "https://p1.aprimocdn.net/solarwinds/bb77cc2e-29a8-46e0-84bf-b23401530e61/2305_SWI_Icon_6i%20Pingdom%20Server%20Monitor_Original%20file.svg",
                      width: 32,
                      height: 32,
                    },
                  },
                },
              ],
              cta: [],
              linksColumnCount: "3",
              links: [
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "SolarWinds Observability Self-Hosted",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/hybrid-cloud-observability",
                  },
                  dlt: "Top Menu",
                  dld: "Products:Monitoring and Observability:SolarWinds Observability Self-Hosted",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Network Performance Monitor",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/network-performance-monitor",
                  },
                  dlt: "Top Menu",
                  dld: "Products:Monitoring and Observability:Network Performance Monitor",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Network Configuration Manager",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/observability/network-configuration-manager",
                  },
                  dlt: "Top Menu",
                  dld: "Products:Monitoring and Observability:Network Configuration Manager",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "SolarWinds Observability SaaS",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/solarwinds-observability",
                  },
                  dlt: "Top Menu",
                  dld: "Products:Monitoring and Observability:SolarWinds Observability SaaS",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "NetFlow Traffic Analyzer",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/netflow-traffic-analyzer",
                  },
                  dlt: "Top Menu",
                  dld: "Products:Monitoring and Observability:NetFlow Traffic Analyzer",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "IP Address Manager",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/observability/ip-address-manager",
                  },
                  dlt: "Top Menu",
                  dld: "Products:Monitoring and Observability:IP Address Manager",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Server Application Monitor",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/server-application-monitor",
                  },
                  dlt: "Top Menu",
                  dld: "Products:Monitoring and Observability:Server Application Monitor",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Virtualization Manager",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/observability/virtualization-manager",
                  },
                  dlt: "Top Menu",
                  dld: "Products:Monitoring and Observability:Virtualization Manager",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Access Rights Manager",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/observability/access-rights-manager",
                  },
                  dlt: "Top Menu",
                  dld: "Products:Monitoring and Observability:Access Rights Manager",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Security Event Manager",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/security-event-manager",
                  },
                  dlt: "Top Menu",
                  dld: "Products:Monitoring and Observability:Security Event Manager",
                },
              ],
            },
            {
              categoryTitle: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Database",
              },
              categoryTitleMobileDLD: "",
              categoryTitleMobileDLT: "",
              categoryDescription: [],
              icon: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Icon",
                    options: {
                      priority: true,
                      alt: "Database Observability icon",
                      source: "aprimo",
                      icon: "https://p1.aprimocdn.net/solarwinds/65584d88-926c-4f21-a77e-b2340153249e/2305_SWI_Icon_3.%20Database%20Management_Original%20file.svg",
                      width: 32,
                      height: 32,
                    },
                  },
                },
              ],
              cta: [],
              linksColumnCount: "2",
              links: [
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Database Performance Analyzer",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/database-performance-analyzer",
                  },
                  dlt: "Top Menu",
                  dld: "Products:Database:Database Performance Analyzer",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "SQL Sentry",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/sql-sentry",
                  },
                  dlt: "Top Menu",
                  dld: "Products:Database:SQL Sentry",
                },
              ],
            },
            {
              categoryTitle: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Incident Response",
              },
              categoryTitleMobileDLD: "",
              categoryTitleMobileDLT: "",
              categoryDescription: [],
              icon: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Icon",
                    options: {
                      priority: true,
                      alt: "Database Observability icon",
                      source: "aprimo",
                      icon: "https://p1.aprimocdn.net/solarwinds/6238acb2-b2f9-4181-8c04-b3a101235290/Icon_-_Incident_Response_Original%20file.svg",
                      width: 32,
                      height: 32,
                    },
                  },
                },
              ],
              cta: [],
              linksColumnCount: "1",
              links: [
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Incident Response by Squadcast",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/it-incident-response-software",
                  },
                  dlt: "Top Menu",
                  dld: "Products:Incident Response:Incident Response by Squadcast",
                },
              ],
            },
            {
              categoryTitle: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "IT Service Management",
              },
              categoryTitleMobileDLD: "",
              categoryTitleMobileDLT: "",
              categoryDescription: [],
              icon: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Icon",
                    options: {
                      priority: true,
                      alt: "Database Observability icon",
                      source: "aprimo",
                      icon: "https://p1.aprimocdn.net/solarwinds/64e33dd6-24f6-453b-973b-b23401530e6f/2305_SWI_Icon_5.%20IT%20Service%20Management_Original%20file.svg",
                      width: 32,
                      height: 32,
                    },
                  },
                },
              ],
              cta: [],
              linksColumnCount: "2",
              links: [
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "SolarWinds Service Desk",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/service-desk",
                  },
                  dlt: "Top Menu",
                  dld: "Products:IT Service Management:SolarWinds Service Desk",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Dameware",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/dameware",
                  },
                  dlt: "Top Menu",
                  dld: "Products:IT Service Management:Dameware",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Web Help Desk",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/web-help-desk",
                  },
                  dlt: "Top Menu",
                  dld: "Products:IT Service Management:Web Help Desk",
                },
              ],
            },
            {
              categoryTitle: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Tools",
              },
              categoryTitleMobileDLD: "",
              categoryTitleMobileDLT: "",
              categoryDescription: [],
              icon: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Icon",
                    options: {
                      priority: true,
                      alt: "Database Observability icon",
                      source: "aprimo",
                      icon: "https://p1.aprimocdn.net/solarwinds/84a698d0-0d6c-40ee-9f98-b23401530de9/2305_SWI_Icon_7.%20Free%20Tools_Original%20file.svg",
                      width: 32,
                      height: 32,
                    },
                  },
                },
              ],
              cta: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Core:Button",
                    options: {
                      variant: "tertiary",
                      icon: "circleArrowRight",
                      iconPosition: "right",
                      size: "m",
                      text: {
                        "@type": "@builder.io/core:LocalizedValue",
                        Default: "View All Products",
                      },
                      href: {
                        "@type": "@builder.io/core:LocalizedValue",
                        Default: "https://www.solarwinds.com/products",
                      },
                      dld: "Products: View All Products link",
                      dlt: "Top Menu",
                    },
                  },
                  responsiveStyles: {
                    large: { marginTop: "var(--spacing-4, 16px)" },
                    medium: {
                      marginTop: "0px",
                      fontSize: "var(--text-link-m, 1.125rem)",
                      lineHeight: "var(--text-link-m--line-height, 1.5rem)",
                    },
                  },
                },
              ],
              linksColumnCount: "2",
              links: [
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Engineer's Toolset",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/engineers-toolset",
                  },
                  dlt: "Top Menu",
                  dld: "Products:Tools:Engineer's Toolset",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Network Topology Mapper",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/network-topology-mapper",
                  },
                  dlt: "Top Menu",
                  dld: "Products:Tools:Network Topology Mapper",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Kiwi Syslog Server",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/kiwi-syslog-server",
                  },
                  dlt: "Top Menu",
                  dld: "Products:Tools:Kiwi Syslog Server",
                },

                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Serv-U Managed File Transfer",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/serv-u-managed-file-transfer-server",
                  },
                  dlt: "Top Menu",
                  dld: "Products:Tools:Serv-U Managed File Transfer",
                },
              ],
            },
          ],
          bottomLinksTitle: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "Not sure where to start?",
          },
          bottomLinks: [
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Get Expert Guidance",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/company/contact-us",
              },
              dlt: "Top Menu",
              dld: "Platform navigation link strip Get Expert Guidance",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Explore Tools",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/tools",
              },
              dlt: "Top Menu",
              dld: "Platform navigation link strip Explore Tools",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Request a Quote",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/onlinequotes",
              },
              dlt: "Top Menu",
              dld: "Platform navigation link strip Request a Quote",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Visit Our THWACK User Community",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://thwack.solarwinds.com/",
              },
              dlt: "Top Menu",
              dld: "Platform navigation link strip Visit Our THWACK User Community",
            },
          ],
        },
        {
          title: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "Solutions",
          },
          dlt: "Top Menu",
          dld: "Solutions",
          leftContent: [
            {
              "@type": "@builder.io/sdk:Element",
              component: {
                name: "Text",
                options: {
                  text: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "<p><strong>Discover our solutions to your IT challenges</strong></p>",
                  },
                },
              },
              meta: {
                localizedTextInputs: ["text"],
                "transformed.text": "localized",
              },
              responsiveStyles: {
                large: {
                  lineHeight: "var(--text-subhead-xl--line-height, 2.5rem)",
                  fontSize: "var(--text-subhead-xl, 1.875rem)",
                  color: "var(--white, #fff)",
                  marginBottom: "var(--spacing-2, 8px)",
                },
              },
            },
            {
              "@type": "@builder.io/sdk:Element",
              component: {
                name: "Text",
                options: {
                  text: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "<p><span>Whether you want to stay ahead with new tech, speed up modernization, support modular services, cut costs, or stay competitive, we’ve built industry-leading software to maximize hybrid IT, spark innovation, and tackle your toughest IT challenges.</span></p>",
                  },
                },
              },
              meta: {
                localizedTextInputs: ["text"],
                "transformed.text": "localized",
              },
              responsiveStyles: {
                large: {
                  lineHeight: "var(--text-body-s--line-height, 1.5rem)",
                  fontSize: "var(--text-body-s, 1rem)",
                  color: "var(--white, #fff)",
                  marginBottom: "var(--spacing-6, 24px)",
                },
              },
            },
          ],
          leftCta: [
            {
              "@type": "@builder.io/sdk:Element",
              component: {
                name: "Core:Button",
                options: {
                  variant: "primary",
                  size: "m",
                  text: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "All Solutions",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/solutions",
                  },
                  theme: "black",
                  dld: "solutions explore solarwinds all solutions button",
                  dlt: "Top Menu",
                },
              },
              responsiveStyles: {
                large: {
                  marginBottom: "var(--spacing-10, 40px)",
                  width: "fit-content",
                },
                medium: { display: "none" },
                small: { display: "none" },
              },
            },
            {
              "@type": "@builder.io/sdk:Element",
              component: {
                name: "Core:Button",
                options: {
                  variant: "secondary",
                  size: "m",
                  text: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Platform Overview",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/solarwinds-platform",
                  },
                  theme: "white",
                  dld: "platform overview button solarwinds platform",
                  dlt: "Top Menu",
                },
              },
              responsiveStyles: {
                large: {
                  marginBottom: "var(--spacing-10, 40px)",
                  display: "none",
                },
                medium: { display: "flex", maxWidth: "fit-content" },
                small: { display: "flex" },
              },
            },
          ],
          leftLinksTitle: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "Explore SolarWinds",
          },
          leftLinks: [
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Interactive Demos",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/interactive-demos",
              },
              dlt: "top menu",
              dld: "Platform explore solarwinds Interactive Demos",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Pricing",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/pricing",
              },
              dlt: "top menu",
              dld: "Platform explore solarwinds Pricing",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "About Us",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/company",
              },
              dlt: "top menu",
              dld: "Platform explore solarwinds About Us",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Secure by Design",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default:
                  "https://www.solarwinds.com/secure-by-design-resources",
              },
              dlt: "top menu",
              dld: "Platform explore solarwinds Secure by Design",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "SolarWinds AI",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/ai",
              },
              dlt: "top menu",
              dld: "Platform explore solarwinds AI",
            },
          ],
          categoryLinksInTwoColumns: false,
          categoryLinks: [
            {
              categoryTitle: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "By Need",
              },
              categoryTitleMobileDLD: "",
              categoryTitleMobileDLT: "",
              categoryDescription: [],
              icon: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Icon",
                    options: {
                      priority: true,
                      alt: "Monitoring and Observability icon",
                      source: "aprimo",
                      icon: "https://p1.aprimocdn.net/solarwinds/31678b35-9267-4002-8dc0-b23401532081/2305_SWI_Icon_Theme%20-%20Analytics%20Reporting_Original%20file.svg",
                      width: 32,
                      height: 32,
                    },
                  },
                },
              ],
              cta: [],
              linksColumnCount: "3",
              links: [
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Application Performance",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/solutions/solarwinds-observability/apm",
                  },
                  dlt: "Top Menu",
                  dld: "Solutions:By Need:Application Performance",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Cloud Migration",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/solutions/cloud-migration",
                  },
                  dlt: "Top Menu",
                  dld: "Solutions:By Need:Cloud Migration",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Database Management",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/database",
                  },
                  dlt: "Top Menu",
                  dld: "Solutions:By Need:Database Management",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "DevOps",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/solutions/solarwinds-observability/devops",
                  },
                  dlt: "Top Menu",
                  dld: "Solutions:By Need:DevOps",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Digital Experience",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/solutions/solarwinds-observability/dem",
                  },
                  dlt: "Top Menu",
                  dld: "Solutions:By Need:Digital Experience",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Infrastructure Monitoring",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/solutions/infrastructure-solutions",
                  },
                  dlt: "Top Menu",
                  dld: "Solutions:By Need:Infrastructure Monitoring",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "IT Service Management",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/solutions/itsm-solutions",
                  },
                  dlt: "Top Menu",
                  dld: "Solutions:By Need:IT Service Management",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "IT Asset Management",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/solutions/it-asset-management",
                  },
                  dlt: "Top Menu",
                  dld: "Solutions:By Need:IT Asset Management",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Log Management",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/solutions/solarwinds-observability/log-management",
                  },
                  dlt: "Top Menu",
                  dld: "Solutions:By Need:Log Management",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Network Monitoring and Management",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/solutions/network-solutions",
                  },
                  dlt: "Top Menu",
                  dld: "Solutions:By Need:Network Monitoring and Management",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "User Experience Monitoring",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/solutions/user-experience-monitoring",
                  },
                  dlt: "Top Menu",
                  dld: "Solutions:By Need:User Experience Monitoring",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "IT Automation",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/solutions/it-automation-software-solutions",
                  },
                  dlt: "Top Menu",
                  dld: "Solutions:By Need:IT Automation",
                },
              ],
            },
            {
              categoryTitle: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "By Technology",
              },
              categoryTitleMobileDLD: "",
              categoryTitleMobileDLT: "",
              categoryDescription: [],
              icon: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Icon",
                    options: {
                      priority: true,
                      alt: "Monitoring and Observability icon",
                      source: "aprimo",
                      icon: "https://p1.aprimocdn.net/solarwinds/ca5624c5-6b16-456e-aa7d-b23401532084/2305_SWI_Icon_System%20Requirements%20-%20CPU_Original%20file.svg",
                      width: 32,
                      height: 32,
                    },
                  },
                },
              ],
              cta: [],
              linksColumnCount: "2",
              links: [
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "AWS",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/solutions/aws-monitoring",
                  },
                  dlt: "Top Menu",
                  dld: "Solutions:By Technology:AWS",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Azure",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/solutions/azure-monitoring",
                  },
                  dlt: "Top Menu",
                  dld: "Solutions:By Technology:Azure",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Kubernetes",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/solutions/solarwinds-observability/kubernetes",
                  },
                  dlt: "Top Menu",
                  dld: "Solutions:By Technology:Kubernetes",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "MySQL",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/solutions/mysql-solutions",
                  },
                  dlt: "Top Menu",
                  dld: "Solutions:By Technology:MySQL",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "PostgreSQL",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/solutions/postgres",
                  },
                  dlt: "Top Menu",
                  dld: "Solutions:By Technology:PostgreSQL",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "SQL Server Diagnostic",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/solutions/sql-diagnostic-solutions",
                  },
                  dlt: "Top Menu",
                  dld: "Solutions:By Technology:SQL Server Diagnostic",
                },
              ],
            },
            {
              categoryTitle: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "By Industry",
              },
              categoryTitleMobileDLD: "",
              categoryTitleMobileDLT: "",
              categoryDescription: [],
              icon: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Icon",
                    options: {
                      priority: true,
                      alt: "Monitoring and Observability icon",
                      source: "aprimo",
                      icon: "https://p1.aprimocdn.net/solarwinds/80745dc2-7403-4ae0-a0cf-b234015327b7/2305_SWI_Icon_Misc%20-%20office_Original%20file.svg",
                      width: 32,
                      height: 32,
                    },
                  },
                },
              ],
              cta: [],
              linksColumnCount: "2",
              links: [
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Education",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/solutions/education-it-solutions",
                  },
                  dlt: "Top Menu",
                  dld: "Solutions:By Industry:Education",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Enterprise",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/solutions/enterprise-solutions",
                  },
                  dlt: "Top Menu",
                  dld: "Solutions:By Industry:Enterprise",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Public Sector",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/public-sector/it-management-solutions-for-government",
                  },
                  dlt: "Top Menu",
                  dld: "Solutions:By Industry:Public Sector",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Small Business",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/solutions/small-business-it-solutions",
                  },
                  dlt: "Top Menu",
                  dld: "Solutions:By Industry:Small Business",
                },
              ],
            },
            {
              categoryTitle: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Partner Solutions",
              },
              categoryTitleMobileDLD: "",
              categoryTitleMobileDLT: "",
              categoryDescription: [],
              icon: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Icon",
                    options: {
                      priority: true,
                      alt: "Monitoring and Observability icon",
                      source: "aprimo",
                      icon: "https://p1.aprimocdn.net/solarwinds/2f05ada7-f158-4313-b562-b23401530713/2305_SWI_Icon_HR%20-%20Business%20Partners_Original%20file.svg",
                      width: 32,
                      height: 32,
                    },
                  },
                },
              ],
              cta: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Core:Button",
                    options: {
                      variant: "tertiary",
                      icon: "circleArrowRight",
                      iconPosition: "right",
                      size: "m",
                      text: {
                        "@type": "@builder.io/core:LocalizedValue",
                        Default: "View All Solutions",
                      },
                      href: {
                        "@type": "@builder.io/core:LocalizedValue",
                        Default: "https://www.solarwinds.com/solutions",
                      },
                      dld: "Solutions link view all solutions link",
                      dlt: "Top Menu",
                    },
                  },
                  responsiveStyles: {
                    large: { marginTop: "var(--spacing-4, 16px)" },
                    medium: {
                      marginTop: "0px",
                      fontSize: "var(--text-link-m, 1.125rem)",
                      lineHeight: "var(--text-link-m--line-height, 1.5rem)",
                    },
                  },
                },
              ],
              linksColumnCount: "2",
              links: [
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Global System Integrators & MSPs",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/partners/global-system-integrators",
                  },
                  dlt: "Top Menu",
                  dld: "Solutions:Partner Solutions:Global System Integrators & MSPs",
                },
              ],
            },
          ],
          bottomLinksTitle: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "Not sure where to start?",
          },
          bottomLinks: [
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Get Expert Guidance",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/company/contact-us",
              },
              dlt: "Top Menu",
              dld: "Platform navigation link strip Get Expert Guidance",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Explore Tools",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/tools",
              },
              dlt: "Top Menu",
              dld: "Platform navigation link strip Explore Tools",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Request a Quote",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/onlinequotes",
              },
              dlt: "Top Menu",
              dld: "Platform navigation link strip Request a Quote",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Visit Our THWACK User Community",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://thwack.solarwinds.com/",
              },
              dlt: "Top Menu",
              dld: "Platform navigation link strip Visit Our THWACK User Community",
            },
          ],
        },
        {
          title: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "Pricing",
          },
          href: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "https://www.solarwinds.com/pricing",
          },
          dlt: "Top Menu",
          dld: "Pricing",
          leftContent: [],
          leftCta: [],
          leftLinksTitle: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "",
          },
          leftLinks: [],
          categoryLinksInTwoColumns: false,
          categoryLinks: [],
          bottomLinksTitle: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "",
          },
          bottomLinks: [],
        },
        {
          title: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "Resources & Support",
          },
          dlt: "Top Menu",
          dld: "Resources & Support",
          leftContent: [
            {
              "@type": "@builder.io/sdk:Element",
              component: {
                name: "Text",
                options: {
                  text: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "<p><strong>We’re here to help you.</strong></p>",
                  },
                },
              },
              meta: {
                localizedTextInputs: ["text"],
                "transformed.text": "localized",
              },
              responsiveStyles: {
                large: {
                  lineHeight: "var(--text-subhead-xl--line-height, 2.5rem)",
                  fontSize: "var(--text-subhead-xl, 1.875rem)",
                  color: "var(--white, #fff)",
                  marginBottom: "var(--spacing-2, 8px)",
                },
              },
            },
            {
              "@type": "@builder.io/sdk:Element",
              component: {
                name: "Text",
                options: {
                  text: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "<p><span>With award-winning technical support, extensive documentation, comprehensive training, and tons of educational assets, we’re dedicated to your success. Plus, you can connect directly with product experts in our user community of over 200,000 registered members.</span></p>",
                  },
                },
              },
              meta: {
                localizedTextInputs: ["text"],
                "transformed.text": "localized",
              },
              responsiveStyles: {
                large: {
                  lineHeight: "var(--text-body-s--line-height, 1.5rem)",
                  fontSize: "var(--text-body-s, 1rem)",
                  color: "var(--white, #fff)",
                  marginBottom: "var(--spacing-6, 24px)",
                },
              },
            },
          ],
          leftCta: [
            {
              "@type": "@builder.io/sdk:Element",
              component: {
                name: "Core:Button",
                options: {
                  variant: "primary",
                  size: "m",
                  text: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Get Expert Guidance",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/company/contact-us",
                  },
                  theme: "black",
                  dld: "Resources & Support Get Expert Guidance Button",
                  dlt: "Top Menu",
                },
              },
              responsiveStyles: {
                large: {
                  marginBottom: "var(--spacing-10, 40px)",
                  width: "fit-content",
                },
                medium: { display: "none" },
                small: { display: "none" },
              },
            },
            {
              "@type": "@builder.io/sdk:Element",
              component: {
                name: "Core:Button",
                options: {
                  variant: "secondary",
                  size: "m",
                  text: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Platform Overview",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/solarwinds-platform",
                  },
                  theme: "white",
                  dld: "platform overview button solarwinds platform",
                  dlt: "Top Menu",
                },
              },
              responsiveStyles: {
                large: {
                  marginBottom: "var(--spacing-10, 40px)",
                  display: "none",
                },
                medium: { display: "flex", maxWidth: "fit-content" },
                small: { display: "flex" },
              },
            },
          ],
          leftLinksTitle: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "Explore SolarWinds",
          },
          leftLinks: [
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Interactive Demos",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/interactive-demos",
              },
              dlt: "top menu",
              dld: "Platform explore solarwinds Interactive Demos",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Pricing",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/pricing",
              },
              dlt: "top menu",
              dld: "Platform explore solarwinds Pricing",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "About Us",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/company",
              },
              dlt: "top menu",
              dld: "Platform explore solarwinds About Us",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Secure by Design",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default:
                  "https://www.solarwinds.com/secure-by-design-resources",
              },
              dlt: "top menu",
              dld: "Platform explore solarwinds Secure by Design",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "SolarWinds AI",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/ai",
              },
              dlt: "top menu",
              dld: "Platform explore solarwinds AI",
            },
          ],
          categoryLinksInTwoColumns: false,
          categoryLinks: [
            {
              categoryTitle: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Resource Center",
              },
              categoryTitleMobileDLD: "",
              categoryTitleMobileDLT: "",
              categoryDescription: [],
              icon: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Icon",
                    options: {
                      priority: true,
                      alt: "Monitoring and Observability icon",
                      source: "aprimo",
                      icon: "https://p1.aprimocdn.net/solarwinds/f0d8a772-6fd2-407f-9d28-b23401531615/2305_SWI_Icon_Resource%20-%20%20Case%20Study_Original%20file.svg",
                      width: 32,
                      height: 32,
                    },
                  },
                },
              ],
              cta: [],
              linksColumnCount: "3",
              links: [
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Newsroom",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/company/newsroom",
                  },
                  dlt: "Top Menu",
                  dld: "Resources & Support:Resource Center:Newsroom",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "SolarWinds Blog",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/blog",
                  },
                  dlt: "Top Menu",
                  dld: "Resources & Support:Resource Center:SolarWinds Blog",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Resources",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/resources",
                  },
                  dlt: "Top Menu",
                  dld: "Resources & Support:Resource Center:Resources",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Investor Relations",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/company",
                  },
                  dlt: "Top Menu",
                  dld: "Resources & Support:Resource Center:Investor Relations",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Trust Center",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/trust-center",
                  },
                  dlt: "Top Menu",
                  dld: "Resources & Support:Resource Center:Trust Center",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "GDPR",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/general-data-protection-regulation-core-it",
                  },
                  dlt: "Top Menu",
                  dld: "Resources & Support:Resource Center:GDPR",
                },
              ],
            },
            {
              categoryTitle: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Services & Support",
              },
              categoryTitleMobileDLD: "",
              categoryTitleMobileDLT: "",
              categoryDescription: [],
              icon: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Icon",
                    options: {
                      priority: true,
                      alt: "Monitoring and Observability icon",
                      source: "aprimo",
                      icon: "https://p1.aprimocdn.net/solarwinds/4270e138-2285-40a7-a28c-b23401531900/2305_SWI_Icon_DeploymentServices_SupportTier_StandardSupport_Original%20file.svg",
                      width: 32,
                      height: 32,
                    },
                  },
                },
              ],
              cta: [],
              linksColumnCount: "3",
              links: [
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Customer Success",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://support.solarwinds.com/?_gl=1*f2gmhd*_gcl_au*MTA4MDEyMTI5My4xNzY1Nzg0NTQ3*_ga_154YSB0XBC*czE3NjU3ODQ1NDEkbzEkZzAkdDE3NjU3ODQ2OTYkajYwJGwwJGgw*_ga_C76HBJ0Y1K*czE3NjU3ODQ1NDIkbzEkZzAkdDE3NjU3ODQ2OTYkajYwJGwwJGgw*_ga_HCDHFKHBVP*czE3NjU3ODQ1NDIkbzEkZzAkdDE3NjU3ODQ2OTYkajYwJGwwJGgw*_ga_4NFWTRKKC5*czE3NjU3ODQ1NDIkbzEkZzAkdDE3NjU3ODQ2OTYkajYwJGwwJGgw",
                  },
                  dlt: "Top Menu",
                  dld: "Resources & Support:Services & Support:Customer Success",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Technical Docs",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://documentation.solarwinds.com/?_gl=1*imnyyo*_gcl_au*MTA4MDEyMTI5My4xNzY1Nzg0NTQ3*_ga_154YSB0XBC*czE3NjU3ODQ1NDEkbzEkZzAkdDE3NjU3ODQ2OTYkajYwJGwwJGgw",
                  },
                  dlt: "Top Menu",
                  dld: "Resources & Support:Services & Support:Technical Docs",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Technical Support",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://customerportal.solarwinds.com/support/submit-a-ticket?sid=mnavT&_gl=1*eb1t9r*_gcl_au*MTA4MDEyMTI5My4xNzY1Nzg0NTQ3*_ga_154YSB0XBC*czE3NjU3ODQ1NDEkbzEkZzAkdDE3NjU3ODQ2OTYkajYwJGwwJGgw",
                  },
                  dlt: "Top Menu",
                  dld: "Resources & Support:Services & Support:Technical Support",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Deployment Services",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://support.solarwinds.com/deployment-services?_gl=1*1hktjss*_gcl_au*MTA4MDEyMTI5My4xNzY1Nzg0NTQ3*_ga_154YSB0XBC*czE3NjU3ODQ1NDEkbzEkZzAkdDE3NjU3ODQ2OTYkajYwJGwwJGgw*_ga_C76HBJ0Y1K*czE3NjU3ODQ1NDIkbzEkZzAkdDE3NjU3ODQ2OTYkajYwJGwwJGgw*_ga_HCDHFKHBVP*czE3NjU3ODQ1NDIkbzEkZzAkdDE3NjU3ODQ2OTYkajYwJGwwJGgw*_ga_4NFWTRKKC5*czE3NjU3ODQ1NDIkbzEkZzAkdDE3NjU3ODQ2OTYkajYwJGwwJGgw",
                  },
                  dlt: "Top Menu",
                  dld: "Resources & Support:Services & Support:Deployment Services",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Renew Maintenance",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://support.solarwinds.com/maintenance?_gl=1*1hktjss*_gcl_au*MTA4MDEyMTI5My4xNzY1Nzg0NTQ3*_ga_154YSB0XBC*czE3NjU3ODQ1NDEkbzEkZzAkdDE3NjU3ODQ2OTYkajYwJGwwJGgw*_ga_C76HBJ0Y1K*czE3NjU3ODQ1NDIkbzEkZzAkdDE3NjU3ODQ2OTYkajYwJGwwJGgw*_ga_HCDHFKHBVP*czE3NjU3ODQ1NDIkbzEkZzAkdDE3NjU3ODQ2OTYkajYwJGwwJGgw*_ga_4NFWTRKKC5*czE3NjU3ODQ1NDIkbzEkZzAkdDE3NjU3ODQ2OTYkajYwJGwwJGgw",
                  },
                  dlt: "Top Menu",
                  dld: "Resources & Support:Services & Support:Renew Maintenance",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Report a Security Incident",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://www.solarwinds.com/trust-center/registration",
                  },
                  dlt: "Top Menu",
                  dld: "Resources & Support:Services & Support:Report a Security Incident",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Premium Support",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://support.solarwinds.com/premium-support-offerings?_gl=1*swofzw*_gcl_au*MTA4MDEyMTI5My4xNzY1Nzg0NTQ3*_ga_154YSB0XBC*czE3NjU3ODQ1NDEkbzEkZzAkdDE3NjU3ODQ2OTYkajYwJGwwJGgw*_ga_C76HBJ0Y1K*czE3NjU3ODQ1NDIkbzEkZzAkdDE3NjU3ODQ2OTYkajYwJGwwJGgw*_ga_HCDHFKHBVP*czE3NjU3ODQ1NDIkbzEkZzAkdDE3NjU3ODQ2OTYkajYwJGwwJGgw*_ga_4NFWTRKKC5*czE3NjU3ODQ1NDIkbzEkZzAkdDE3NjU3ODQ2OTYkajYwJGwwJGgw",
                  },
                  dlt: "Top Menu",
                  dld: "Resources & Support:Services & Support:Premium Support",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "SolarWinds Academy: Training & Certification",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://support.solarwinds.com/solarwinds-academy?_gl=1*188wgnl*_gcl_au*MTA4MDEyMTI5My4xNzY1Nzg0NTQ3*_ga_154YSB0XBC*czE3NjU3ODQ1NDEkbzEkZzAkdDE3NjU3ODQ2OTYkajYwJGwwJGgw*_ga_C76HBJ0Y1K*czE3NjU3ODQ1NDIkbzEkZzAkdDE3NjU3ODQ2OTYkajYwJGwwJGgw*_ga_HCDHFKHBVP*czE3NjU3ODQ1NDIkbzEkZzAkdDE3NjU3ODQ2OTYkajYwJGwwJGgw*_ga_4NFWTRKKC5*czE3NjU3ODQ1NDIkbzEkZzAkdDE3NjU3ODQ2OTYkajYwJGwwJGgw",
                  },
                  dlt: "Top Menu",
                  dld: "Resources & Support:Services & Support:SolarWinds Academy: Training & Certification",
                },
              ],
            },
            {
              categoryTitle: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Community",
              },
              categoryTitleMobileDLD: "",
              categoryTitleMobileDLT: "",
              categoryDescription: [],
              icon: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Icon",
                    options: {
                      priority: true,
                      alt: "Monitoring and Observability icon",
                      source: "aprimo",
                      icon: "https://p1.aprimocdn.net/solarwinds/7ebe6250-0dc3-4936-bde5-b23401532481/2305_SWI_Icon_Theme%20-%20FAQ_Original%20file.svg",
                      width: 32,
                      height: 32,
                    },
                  },
                },
              ],
              cta: [],
              linksColumnCount: "1",
              links: [
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "THWACK SolarWinds User Community",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default:
                      "https://thwack.solarwinds.com/?_gl=1*bpekr6*_gcl_au*MTA4MDEyMTI5My4xNzY1Nzg0NTQ3*_ga_154YSB0XBC*czE3NjU3ODQ1NDEkbzEkZzAkdDE3NjU3ODQ2OTYkajYwJGwwJGgw",
                  },
                  dlt: "Top Menu",
                  dld: "Resources & Support:Community:THWACK SolarWinds User Community",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Events",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/events",
                  },
                  dlt: "Top Menu",
                  dld: "Resources & Support:Community:Events",
                },
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Partners",
                  },
                  href: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "https://www.solarwinds.com/partners",
                  },
                  dlt: "Top Menu",
                  dld: "Resources & Support:Community:Partners",
                },
              ],
            },
          ],
          bottomLinksTitle: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "Not sure where to start?",
          },
          bottomLinks: [
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Get Expert Guidance",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/company/contact-us",
              },
              dlt: "Top Menu",
              dld: "Platform navigation link strip Get Expert Guidance",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Explore Tools",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/tools",
              },
              dlt: "Top Menu",
              dld: "Platform navigation link strip Explore Tools",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Request a Quote",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/onlinequotes",
              },
              dlt: "Top Menu",
              dld: "Platform navigation link strip Request a Quote",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Visit Our THWACK User Community",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://thwack.solarwinds.com/",
              },
              dlt: "Top Menu",
              dld: "Platform navigation link strip Visit Our THWACK User Community",
            },
          ],
        },
      ],
    },
  ],
  defaultStyles: {
    width: "100%",
  },
};
